<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import { defineAsyncMicroApp, unmountApp } from "@gohighlevel/ghl-module-federation-plugin";
import { computed, markRaw, onBeforeUnmount, ref, watch } from "vue";

const props = defineProps<{
  show: boolean;
  data: Record<string, unknown> | null;
}>();

const emit = defineEmits(["update:close","update:anyUnsavedChanges"]);
const appStore = useAppStore();
const emitter = useEmitter();
const remoteComponent = ref<any>(null);
let mounted = false;
const QUIZ_RESULT_SAVED_EVENT = "ext-on-quiz-result-builder-saved";
const QUIZ_RESULT_CLOSE_EVENT = "ext-on-quiz-result-builder-modal-close";

const onQuizResultSaved = (payload: Record<string, unknown>) => {
  if (payload.category) {
    appStore.category = payload.category as any;
    delete payload.category;
  }
  if (payload.categories) {
    delete payload.categories;
  }
  if (payload.slides) {
    appStore.slides = payload.slides as any;
    delete payload.slides;
  }
  appStore.updateResultTemplate(payload);
  emit("update:anyUnsavedChanges", true);
  emit("update:close");
};

const onQuizResultClose = () => {
  emit("update:close");
};

const remoteProps = computed(() => ({
  show: props.show,
  emitter,
  store: appStore,
  locale: localStorage.getItem("locale") || "en-US",
  user: {
    id: appStore.userId,
    email: appStore.email,
  },
  data: props.data || {},
  "onUpdate:close": () => emit("update:close"),
}));

const mountRemote = async () => {
  if (mounted) {
    return;
  }
  try {
    const [componentMod] = await defineAsyncMicroApp("quizResultBuilderApp", ["QuizResultBuilder"]);
    remoteComponent.value = markRaw(componentMod);
    mounted = true;
  } catch (error) {
    console.error("Failed to mount QuizResultBuilder remote", error);
  }
};

watch(
  () => props.show,
  (show) => {
    if (show) {
      mountRemote();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  emitter.off(QUIZ_RESULT_SAVED_EVENT, onQuizResultSaved);
  emitter.off(QUIZ_RESULT_CLOSE_EVENT, onQuizResultClose);
  unmountApp("quizResultBuilderApp");
});

emitter.on(QUIZ_RESULT_SAVED_EVENT, onQuizResultSaved);
emitter.on(QUIZ_RESULT_CLOSE_EVENT, onQuizResultClose);
</script>

<template>
  <component :is="remoteComponent" v-if="show && remoteComponent" v-bind="remoteProps" />
</template>
